const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const CONTROLS_HIDE_DELAY = 1000;
const CLICK_DRAG_THRESHOLD = 8;
const CLICK_SCROLL_THRESHOLD = 4;

class HeroMobileSlider extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.intervalId = undefined;
    this.hideControlsTimeout = undefined;
    this.scrollFrame = undefined;
    this.isInteracting = false;
    this.isHovered = false;
    this.hasFocusWithin = false;
    this.clickStart = undefined;
    this.didDragForClick = false;
    this.userAllowsAutoplay = true;
    this.cleanups = [];

    this.handleScroll = this.handleScroll.bind(this);
    this.handleModeChange = this.handleModeChange.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerEnd = this.handlePointerEnd.bind(this);
    this.handleHeroClick = this.handleHeroClick.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handlePreviousClick = this.handleArrowClick.bind(this, -1);
    this.handleNextClick = this.handleArrowClick.bind(this, 1);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-mobile-slider-track]');
    this.slides = [...this.querySelectorAll('[data-mobile-slider-slide]')];
    this.progress = this.querySelector('[data-mobile-slider-progress]');
    this.progressIndicator = this.querySelector('[data-mobile-slider-progress-indicator]');
    this.previousButton = this.querySelector('[data-mobile-slider-prev]');
    this.nextButton = this.querySelector('[data-mobile-slider-next]');
    this.toggleButton = this.querySelector('[data-mobile-slider-toggle]');
    this.pauseIcon = this.querySelector('[data-mobile-slider-pause-icon]');
    this.playIcon = this.querySelector('[data-mobile-slider-play-icon]');
    this.currentCounter = this.querySelector('[data-mobile-slider-current]');

    if (!this.track || this.slides.length === 0) return;

    this.abortController = new AbortController();
    this.reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);

    this.track.addEventListener('scroll', this.handleScroll, { passive: true, signal: this.abortController.signal });
    this.track.addEventListener('pointerdown', this.handlePointerDown, {
      passive: true,
      signal: this.abortController.signal,
    });
    this.track.addEventListener('pointermove', this.handlePointerMove, {
      passive: true,
      signal: this.abortController.signal,
    });
    this.addEventListener('click', this.handleHeroClick, {
      signal: this.abortController.signal,
    });
    this.addEventListener('auxclick', this.handleHeroClick, {
      signal: this.abortController.signal,
    });
    this.addEventListener('mouseenter', this.handleMouseEnter, {
      signal: this.abortController.signal,
    });
    this.addEventListener('mouseleave', this.handleMouseLeave, {
      signal: this.abortController.signal,
    });
    this.addEventListener('focusin', this.handleFocusIn, {
      signal: this.abortController.signal,
    });
    this.addEventListener('focusout', this.handleFocusOut, {
      signal: this.abortController.signal,
    });

    window.addEventListener('pointerup', this.handlePointerEnd, { passive: true, signal: this.abortController.signal });
    window.addEventListener('pointercancel', this.handlePointerEnd, { passive: true, signal: this.abortController.signal });

    this.previousButton?.addEventListener('click', this.handlePreviousClick, {
      signal: this.abortController.signal,
    });
    this.nextButton?.addEventListener('click', this.handleNextClick, {
      signal: this.abortController.signal,
    });
    this.toggleButton?.addEventListener('click', this.handleToggleClick, {
      signal: this.abortController.signal,
    });

    document.addEventListener('visibilitychange', this.handleVisibilityChange, {
      signal: this.abortController.signal,
    });
    window.addEventListener('resize', this.handleModeChange, {
      signal: this.abortController.signal,
    });
    window.addEventListener('orientationchange', this.handleModeChange, {
      signal: this.abortController.signal,
    });

    this.addMediaQueryListener(this.reducedMotionMedia, this.handleModeChange);

    this.updateState(0);
    this.updateMode();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    window.clearTimeout(this.hideControlsTimeout);
    window.cancelAnimationFrame(this.scrollFrame);
    this.abortController?.abort();
    this.cleanups.forEach((cleanup) => cleanup());
    this.cleanups = [];
  }

  addMediaQueryListener(mediaQuery, callback) {
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', callback, { signal: this.abortController.signal });
      return;
    }

    mediaQuery.addListener(callback);
    this.cleanups.push(() => mediaQuery.removeListener(callback));
  }

  get autoplayInterval() {
    const value = Number.parseInt(this.dataset.autoplayInterval || '', 10);

    if (Number.isNaN(value)) return 4000;

    return Math.max(value, 3000);
  }

  get shouldAutoplay() {
    return (
      this.userAllowsAutoplay &&
      this.isActive() &&
      !this.isInteracting &&
      !this.isHovered &&
      !this.hasFocusWithin &&
      !this.reducedMotionMedia?.matches &&
      this.slides.length > 1 &&
      !document.hidden
    );
  }

  updateMode() {
    if (!this.track) return;

    if (!this.isActive()) {
      this.stopAutoplay();
      this.hideControls();
      this.pauseAllMedia();
      return;
    }

    this.goTo(this.currentIndex, 'auto');

    this.refreshAutoplay();
  }

  refreshAutoplay() {
    if (this.shouldAutoplay) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }
  }

  startAutoplay() {
    if (this.intervalId || !this.shouldAutoplay) return;

    this.intervalId = window.setInterval(() => {
      if (!this.shouldAutoplay) {
        this.stopAutoplay();
        return;
      }

      if (this.isInteracting) return;

      this.goTo(this.currentIndex + 1);
    }, this.autoplayInterval);

    this.updateAutoplayControl();
  }

  stopAutoplay() {
    window.clearInterval(this.intervalId);
    this.intervalId = undefined;
    this.updateAutoplayControl();
  }

  handlePointerDown(event) {
    this.clickStart = {
      x: event.clientX,
      y: event.clientY,
      scrollLeft: this.track?.scrollLeft || 0,
    };
    this.didDragForClick = false;

    if (!this.isActive() || this.slides.length <= 1) return;

    this.isInteracting = true;
    this.userAllowsAutoplay = false;
    this.stopAutoplay();
    this.showControls();
  }

  handlePointerMove(event) {
    if (!this.clickStart) return;

    const movedX = Math.abs(event.clientX - this.clickStart.x);
    const movedY = Math.abs(event.clientY - this.clickStart.y);

    if (movedX > CLICK_DRAG_THRESHOLD || movedY > CLICK_DRAG_THRESHOLD) {
      this.didDragForClick = true;
    }
  }

  handlePointerEnd() {
    if (!this.isInteracting) return;

    this.isInteracting = false;
    this.scheduleControlsHide();
  }

  handleHeroClick(event) {
    const slideLink = event.target instanceof Element ? event.target.closest('[data-hero-slide-link]') : null;
    if (!slideLink || event.defaultPrevented) return;

    if (this.didDragForClick) {
      event.preventDefault();
      event.stopPropagation();
      this.resetClickTracking();
      return;
    }

    this.resetClickTracking();
  }

  handleMouseEnter() {
    this.isHovered = true;
    this.refreshAutoplay();
  }

  handleMouseLeave() {
    this.isHovered = false;
    this.refreshAutoplay();
  }

  handleFocusIn() {
    this.hasFocusWithin = true;
    this.refreshAutoplay();
  }

  handleFocusOut(event) {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.hasFocusWithin = false;
    this.refreshAutoplay();
  }

  handleArrowClick(direction, event) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isActive() || this.slides.length <= 1) return;

    this.userAllowsAutoplay = false;
    this.stopAutoplay();
    this.showControls();
    this.goTo(this.currentIndex + direction);
    this.scheduleControlsHide();
  }

  handleToggleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.userAllowsAutoplay) {
      this.userAllowsAutoplay = false;
      this.stopAutoplay();
      return;
    }

    this.userAllowsAutoplay = true;
    this.refreshAutoplay();
  }

  handleScroll() {
    if (!this.isActive()) return;

    window.cancelAnimationFrame(this.scrollFrame);

    this.scrollFrame = window.requestAnimationFrame(() => {
      if (this.clickStart && this.track) {
        const scrolled = Math.abs(this.track.scrollLeft - this.clickStart.scrollLeft);

        if (scrolled > CLICK_SCROLL_THRESHOLD) {
          this.didDragForClick = true;
        }
      }

      this.syncFromScroll();

      if (this.isInteracting) {
        this.showControls();
      }
    });
  }

  handleModeChange() {
    this.updateMode();
  }

  handleVisibilityChange() {
    this.updateMode();
  }

  isActive() {
    if (!this.isConnected) return false;

    const styles = window.getComputedStyle(this);

    return styles.display !== 'none' && styles.visibility !== 'hidden' && this.getClientRects().length > 0;
  }

  goTo(index, behavior = 'smooth') {
    if (!this.track || this.slides.length === 0) return;

    const nextIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    const nextSlide = this.slides[nextIndex];

    if (!nextSlide) return;

    const scrollBehavior = this.reducedMotionMedia?.matches ? 'auto' : behavior;
    this.track.scrollTo({ left: nextSlide.offsetLeft, behavior: scrollBehavior });
    this.updateState(nextIndex);
  }

  syncFromScroll() {
    if (!this.track || this.slides.length === 0) return;

    const trackWidth = this.track.clientWidth;
    if (trackWidth <= 0) return;

    const nextIndex = Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / trackWidth)));
    this.updateState(nextIndex);
  }

  showControls() {
    if (this.slides.length <= 1) return;

    window.clearTimeout(this.hideControlsTimeout);
    this.classList.add('is-interacting');
  }

  scheduleControlsHide() {
    window.clearTimeout(this.hideControlsTimeout);
    this.hideControlsTimeout = window.setTimeout(() => {
      this.hideControls();
    }, CONTROLS_HIDE_DELAY);
  }

  hideControls() {
    window.clearTimeout(this.hideControlsTimeout);
    this.classList.remove('is-interacting');
  }

  resetClickTracking() {
    this.clickStart = undefined;
    this.didDragForClick = false;
  }

  updateProgress() {
    if (!this.progress || !this.progressIndicator || this.slides.length <= 1) return;

    const segmentWidth = 100 / this.slides.length;
    this.progressIndicator.style.setProperty('--hero-mobile-slider-progress-width', `${segmentWidth}%`);
    this.progressIndicator.style.setProperty('--hero-mobile-slider-progress-x', `${this.currentIndex * 100}%`);
  }

  updateAutoplayControl() {
    if (!this.toggleButton) return;

    const reducedMotion = Boolean(this.reducedMotionMedia?.matches);
    const isPausedByUser = !this.userAllowsAutoplay;

    this.toggleButton.hidden = reducedMotion;
    this.toggleButton.setAttribute('aria-label', isPausedByUser ? 'Play slideshow' : 'Pause slideshow');
    this.toggleButton.setAttribute('aria-pressed', String(isPausedByUser));

    if (this.pauseIcon) this.pauseIcon.hidden = isPausedByUser;
    if (this.playIcon) this.playIcon.hidden = !isPausedByUser;
  }

  syncMediaPlayback() {
    if (!this.isActive()) {
      this.pauseAllMedia();
      return;
    }

    this.slides.forEach((slide, slideIndex) => {
      const videos = slide.querySelectorAll('video');
      if (videos.length === 0) return;

      videos.forEach((video) => {
        if (slideIndex === this.currentIndex) {
          const playPromise = video.play();
          if (playPromise?.catch) {
            playPromise.catch(() => {});
          }
        } else {
          video.pause();
        }
      });
    });
  }

  pauseAllMedia() {
    this.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
  }

  updateState(index) {
    this.currentIndex = index;

    if (this.currentCounter) {
      this.currentCounter.textContent = String(index + 1);
    }

    this.slides.forEach((slide, slideIndex) => {
      const isActiveSlide = slideIndex === index;
      slide.setAttribute('aria-hidden', isActiveSlide ? 'false' : 'true');
      slide.querySelectorAll('[data-hero-slide-link]').forEach((link) => {
        if (isActiveSlide) {
          link.removeAttribute('tabindex');
        } else {
          link.setAttribute('tabindex', '-1');
        }
      });
    });

    this.updateProgress();
    this.updateAutoplayControl();
    this.syncMediaPlayback();
  }
}

if (!customElements.get('hero-mobile-slider')) {
  customElements.define('hero-mobile-slider', HeroMobileSlider);
}

if (!window.__owHeroMobileSliderEventsBound) {
  window.__owHeroMobileSliderEventsBound = true;

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element)) return;

    event.target.querySelectorAll('hero-mobile-slider').forEach((slider) => {
      slider.updateMode?.();
    });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!(event.target instanceof Element)) return;

    event.target.querySelectorAll('hero-mobile-slider').forEach((slider) => {
      slider.stopAutoplay?.();
      slider.hideControls?.();
    });
  });
}
